<script setup lang="ts">
/**
 * CategoryAccessDialog — het slot van één categorie instellen.
 *
 * Bereikbaar via het ⋯-menu van een categorie (hoofdmenu en linkermenu).
 * De standaard is "volgt de site": pas als je daarvan afwijkt zet deze
 * categorie een eigen regel, die subcategorieën en pagina's eronder erven.
 *
 * Per-categorie-sloten zijn een Starter-functie. Op het gratis plan zijn de
 * afwijkende standen uitgeschakeld, maar "volgt de site" blijft altijd
 * kiesbaar — anders zou een account na een downgrade zijn eigen instelling
 * niet meer ongedaan kunnen maken.
 */

import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { Eye, EyeOff, RefreshCw, X } from 'lucide-vue-next'
import { useFocusManagement } from '../../composables/useFocusManagement'
import { useAnnounce } from '../../composables/useAnnounce'
import { useSectionStore } from '../../stores/sections'
import { useAccessStore } from '../../stores/access'
import { useNotificationStore } from '../../stores/notifications'
import { generatePassphrase } from '../../utils/passphrase'
import type { Section } from '../../types'

const props = defineProps<{
  section: Section | null
}>()

const emit = defineEmits<{ close: [] }>()

const store = useSectionStore()
const access = useAccessStore()
const notifications = useNotificationStore()
const { t } = useI18n()

type Mode = 'inherit' | 'public' | 'password' | 'team'

const mode = ref<Mode>('inherit')
const password = ref('')
const hint = ref('')
const visibility = ref<'locked' | 'hidden'>('locked')
const showPassword = ref(false)
const isSaving = ref(false)

const hasExistingPassword = computed(() => Boolean(props.section?.hasAccessPassword))
const isOpen = computed(() => props.section !== null)

/** Wat "volgt de site" op dit moment betekent — anders is het een gok. */
const inheritedLabel = computed(() => {
  switch (access.mode) {
    case 'password': return t('access.modes.password')
    case 'team': return t('access.modes.team')
    default: return t('access.modes.public')
  }
})

const needsPassword = computed(() => mode.value === 'password')
const showsVisibilityChoice = computed(() => mode.value === 'password' || mode.value === 'team')

const canSave = computed(() => {
  if (isSaving.value) return false
  if (needsPassword.value && !hasExistingPassword.value && password.value.trim().length < 8) return false
  return true
})

/** null = nog onbekend; de radio's blokkeren pas als het echt niet mag. */
const available = computed(() => access.categoryLocksAvailable !== false)

watch(() => props.section, (section) => {
  if (!section) return
  mode.value = (section.accessMode as Mode) ?? 'inherit'
  hint.value = section.accessHint ?? ''
  visibility.value = section.accessVisibility ?? 'locked'
  password.value = ''
  showPassword.value = false
  access.ensurePlanInfo()
}, { immediate: true })

/* ── Dialooggedrag ──────────────────────────────────────────────
   Zelfde opzet als de mobiele navigatiedrawer in App.vue: focus vangen
   zolang de dialoog open staat, focus terug bij sluiten, achtergrond niet
   mee laten scrollen, en de opening aankondigen voor schermlezers. */
const dialogEl = ref<HTMLElement | null>(null)
const { saveFocus, restoreFocus, trapFocus, focusFirst } = useFocusManagement()
const { announceDialogOpen, announceDialogClose } = useAnnounce()
let cleanupTrap: (() => void) | null = null

watch(isOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''

  if (open) {
    saveFocus()
    nextTick(() => {
      if (dialogEl.value) {
        cleanupTrap = trapFocus(dialogEl.value)
        focusFirst(dialogEl.value)
      }
    })
    announceDialogOpen(t('access.category.title', { name: props.section?.name ?? '' }))
  } else {
    cleanupTrap?.()
    cleanupTrap = null
    restoreFocus()
    announceDialogClose()
  }
})

onBeforeUnmount(() => {
  cleanupTrap?.()
  document.body.style.overflow = ''
})

function suggestPassword() {
  password.value = generatePassphrase()
  showPassword.value = true
}

async function save() {
  const section = props.section
  if (!section || !canSave.value) return

  isSaving.value = true
  try {
    await store.updateSectionAccess(section.id, {
      mode: mode.value,
      password: password.value.trim() || null,
      hint: hint.value.trim() || null,
      visibility: visibility.value,
    })
    notifications.success(t('access.category.saved', { name: section.name }))
    emit('close')
  } catch (err: any) {
    notifications.error(
      err?.response?.data?.code === 'plan_required'
        ? t('access.category.planRequired')
        : err?.response?.data?.message || t('access.category.saveFailed')
    )
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        @click.self="emit('close')"
      >
        <div
          ref="dialogEl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="category-access-title"
          class="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl"
          @keydown.esc="emit('close')"
        >
          <div class="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
            <h2 id="category-access-title" class="text-base font-semibold text-gray-900">
              {{ t('access.category.title', { name: section?.name ?? '' }) }}
            </h2>
            <button
              type="button"
              class="-mr-2 -mt-1 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
              :aria-label="t('modal.close')"
              @click="emit('close')"
            >
              <X class="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <div class="flex-1 overflow-y-auto px-6 py-5">
    <div v-if="section" class="space-y-5">
      <p v-if="!available" class="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
        {{ t('access.category.planRequired') }}
      </p>

      <fieldset>
        <legend class="text-sm font-medium text-gray-700">{{ t('access.category.legend') }}</legend>

        <div class="mt-3 space-y-3">
          <label class="flex items-start gap-3 cursor-pointer">
            <input v-model="mode" type="radio" value="inherit" class="mt-0.5 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500" />
            <span>
              <span class="block text-sm font-medium text-gray-800">{{ t('access.category.inherit') }}</span>
              <span class="block text-xs text-gray-500">
                {{ t('access.category.inheritNow', { mode: inheritedLabel }) }}
              </span>
            </span>
          </label>

          <label class="flex items-start gap-3" :class="!available ? 'opacity-50' : 'cursor-pointer'">
            <input v-model="mode" type="radio" value="public" :disabled="!available" class="mt-0.5 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500" />
            <span>
              <span class="block text-sm font-medium text-gray-800">{{ t('access.modes.public') }}</span>
              <span class="block text-xs text-gray-500">{{ t('access.category.publicDesc') }}</span>
            </span>
          </label>

          <label class="flex items-start gap-3" :class="!available ? 'opacity-50' : 'cursor-pointer'">
            <input v-model="mode" type="radio" value="password" :disabled="!available" class="mt-0.5 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500" />
            <span>
              <span class="block text-sm font-medium text-gray-800">{{ t('access.modes.password') }}</span>
              <span class="block text-xs text-gray-500">{{ t('access.category.passwordDesc') }}</span>
            </span>
          </label>

          <label class="flex items-start gap-3" :class="!available ? 'opacity-50' : 'cursor-pointer'">
            <input v-model="mode" type="radio" value="team" :disabled="!available" class="mt-0.5 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500" />
            <span>
              <span class="block text-sm font-medium text-gray-800">{{ t('access.modes.team') }}</span>
              <span class="block text-xs text-gray-500">{{ t('access.category.teamDesc') }}</span>
            </span>
          </label>
        </div>
      </fieldset>

      <!-- Wachtwoord -->
      <div v-if="needsPassword" class="rounded-lg bg-gray-50 border border-gray-200 p-4">
        <label for="category-access-password" class="block text-sm font-medium text-gray-700">
          {{ hasExistingPassword ? t('access.category.newPassword') : t('access.gate.passwordLabel') }}
        </label>
        <div class="mt-1 flex items-center gap-2">
          <input
            id="category-access-password"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="new-password"
            :placeholder="hasExistingPassword ? t('access.category.keepPassword') : ''"
            class="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <button
            type="button"
            class="rounded-lg p-2 text-gray-500 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            :aria-label="showPassword ? t('access.settings.hidePassword') : t('access.settings.showPassword')"
            @click="showPassword = !showPassword"
          >
            <component :is="showPassword ? EyeOff : Eye" class="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="rounded-lg p-2 text-gray-500 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            :aria-label="t('access.settings.generate')"
            :title="t('access.settings.generate')"
            @click="suggestPassword"
          >
            <RefreshCw class="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <p class="mt-2 text-xs text-gray-500">{{ t('access.settings.passwordRule') }}</p>

        <label for="category-access-hint" class="mt-4 block text-sm font-medium text-gray-700">
          {{ t('access.settings.hintLabel') }}
        </label>
        <input
          id="category-access-hint"
          v-model="hint"
          type="text"
          maxlength="200"
          :placeholder="t('access.settings.hintPlaceholder')"
          class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <!-- Zichtbaar op slot, of helemaal weg -->
      <fieldset v-if="showsVisibilityChoice">
        <legend class="text-sm font-medium text-gray-700">{{ t('access.category.visibilityLegend') }}</legend>
        <div class="mt-3 space-y-3">
          <label class="flex items-start gap-3 cursor-pointer">
            <input v-model="visibility" type="radio" value="locked" class="mt-0.5 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500" />
            <span>
              <span class="block text-sm font-medium text-gray-800">{{ t('access.category.visibilityLocked') }}</span>
              <span class="block text-xs text-gray-500">{{ t('access.category.visibilityLockedDesc') }}</span>
            </span>
          </label>
          <label class="flex items-start gap-3 cursor-pointer">
            <input v-model="visibility" type="radio" value="hidden" class="mt-0.5 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500" />
            <span>
              <span class="block text-sm font-medium text-gray-800">{{ t('access.category.visibilityHidden') }}</span>
              <span class="block text-xs text-gray-500">{{ t('access.category.visibilityHiddenDesc') }}</span>
            </span>
          </label>
        </div>
      </fieldset>
    </div>

          </div>

          <div class="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <button type="button" class="btn-secondary" @click="emit('close')">{{ t('common.cancel') }}</button>
            <button type="button" class="btn-primary" :disabled="!canSave" @click="save">
              {{ t('common.save') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
